/*
 * H1. The grouping, in one readable place.
 *
 * Every hub below takes pages that already existed and puts them behind one
 * sidebar item. The counts this is aimed at:
 *
 *   Role              before  after
 *   AI Champion         16      7
 *   Owner               15      7
 *   Employee            10      6
 *   Department head      8       6
 *   Trainer              7       6
 *   Expert               4       3
 *   Plain client         5       5
 *
 * Nothing was deleted to get there. Each old address still routes to its page
 * on its own, so a link in a lesson, an email or a bookmark keeps working; the
 * hub is a second way in, not a replacement. And each tab still asks for its
 * own grant, so grouping never widens what anyone can reach.
 */

import React from 'react';
import {
  Award, BarChart3, Boxes, Building2, FileText, GraduationCap,
  LayoutTemplate, ListChecks, Network, Scale, ShieldCheck, SlidersHorizontal,
  Sparkles, TrendingUp, Users, Wrench, HelpCircle,
} from 'lucide-react';

import AdoptionHub, { HubTab } from '../../components/adoption/AdoptionHub';
import { useParticipant } from '../../hooks/useParticipant';

import AaiWorkspace from './AaiWorkspace';
import ImpactDashboardPage from './insights/ImpactDashboardPage';
import RiskGovernancePage from './insights/RiskGovernancePage';
import WhatsAllowedPage from './insights/WhatsAllowedPage';
import HelpCommunityPage from './insights/HelpCommunityPage';
import SpotlightsPage from './SpotlightsPage';
import ScoringSettingsPage from './cockpit/ScoringSettingsPage';
import CurriculumPage from './cockpit/CurriculumPage';
import TemplatesPage from './cockpit/TemplatesPage';
import DepartmentsAdminPage from './cockpit/DepartmentsAdminPage';
import RegistryPage from './RegistryPage';
import VendorEvaluatorPage from './VendorEvaluatorPage';
import PrdReviewsPage from './PrdReviewsPage';
import SkillsMarketplacePage from './SkillsMarketplacePage';
import OrgChartPage from './OrgChartPage';
import MyReadinessPage from './MyReadinessPage';
import OnboardingPage from './OnboardingPage';
import DepartmentPage from './DepartmentPage';
import TeamRollupPage from './TeamRollupPage';
import RosterPage from './RosterPage';

/**
 * The AI-adoption home. One item instead of five: the role's dashboard, plus
 * the company-level views that were their own sidebar rows (AI Impact,
 * Spotlights, Risk & Governance, Scoring Settings). Each one still asks for the
 * grant it always asked for, so a trainer's hub and a champion's hub are
 * different sets under the same item.
 */
export const AdoptionHomeHub: React.FC = () => (
  <div className="max-w-5xl mx-auto px-4 pt-6">
    <AdoptionHub
      label="AI adoption"
      tabs={[
        // AaiWorkspace, not RoleDashboard: it carries the page's own heading, so
        // the overview tab looks exactly like /aai looked before the grouping.
        { id: 'overview', label: 'Overview', icon: BarChart3, render: () => <AaiWorkspace /> },
        { id: 'impact', label: 'AI Impact', icon: TrendingUp, perm: 'aai_view_impact', render: () => <ImpactDashboardPage /> },
        { id: 'spotlights', label: 'Spotlights', icon: Award, render: () => <SpotlightsPage /> },
        { id: 'governance', label: 'Risk & Governance', icon: ShieldCheck, perm: 'aai_view_governance', render: () => <RiskGovernancePage /> },
        { id: 'scoring', label: 'Scoring', icon: SlidersHorizontal, perm: 'aai_manage_program', render: () => <ScoringSettingsPage /> },
      ]}
    />
  </div>
);

/**
 * Use cases. The registry, the vendor evaluator and the PRD review queue were
 * three rows describing one pipeline: what we might build, what we would buy,
 * and what an engineer has cleared.
 */
export const UseCasesHub: React.FC = () => (
  <AdoptionHub
    label="Use cases"
    tabs={[
      { id: 'registry', label: 'Use-Case Registry', icon: FileText, render: () => <RegistryPage /> },
      { id: 'vendors', label: 'Vendor Evaluator', icon: Scale, perm: 'aai_evaluate_vendors', render: () => <VendorEvaluatorPage /> },
      { id: 'reviews', label: 'PRD Reviews', icon: ListChecks, perm: 'aai_review_prd', render: () => <PrdReviewsPage /> },
    ]}
  />
);

/** People. The org chart and the department admin are both "who is where". */
export const PeopleHub: React.FC = () => (
  <AdoptionHub
    label="People"
    tabs={[
      { id: 'chart', label: 'Org Chart & Cohorts', icon: Network, perm: 'aai_view_org_chart', render: () => <OrgChartPage /> },
      { id: 'departments', label: 'Departments', icon: Building2, render: () => <DepartmentsAdminPage /> },
    ]}
  />
);

/**
 * My tools. Four rows on an employee's sidebar (My Tools & Skills, Skill
 * Builder, the marketplace, and for full-workspace roles a second tools page)
 * collapse into one.
 *
 * `enrolledOnly` on the adoption tabs matters here: /my-tools is also the plain
 * client shell's Tool Scout page, and that shell holds no workspace role, so
 * the permission check fails open for them. Without the flag a plain client
 * would be shown three adoption tabs that have nothing behind them.
 */
export const MyToolsHub: React.FC<{
  first: React.ReactNode;
  /** Kept for the callers that pass it. It used to decide whether a second
   *  "Skills & Dives" tab appeared; that tab is gone for everybody now, so the
   *  flag no longer changes anything here. */
  firstIsAdoption?: boolean;
}> = ({ first }) => {
  /* Skills & Dives has gone.
   *
   * It drew the adoption tools page a second time, beside the tools page the
   * first tab already shows, so the hub offered the same screen twice under
   * two names and a person had to open both to learn they were one. What a
   * dive leaves behind is a skill, and skills are the tab next to it.
   *
   * /aai/my-tools still resolves to that page on its own, so nothing that
   * linked to it breaks. */
  const tabs: HubTab[] = [
    { id: 'tools', label: 'My Tools', icon: Wrench, render: () => <>{first}</> },
  ];
  // Skill Builder is no longer a tab: you build a skill from the place that
  // showed you the gap (My Tools, a training module, or the marketplace's own
  // New skill). /skill-builder still resolves, so every one of those CTAs works.
  tabs.push(
    { id: 'marketplace', label: 'Skills & Plugins', icon: Boxes, perm: 'aai_view_marketplace', enrolledOnly: true, render: () => <SkillsMarketplacePage /> },
  );
  return <AdoptionHub label="My tools" tabs={tabs} />;
};

/**
 * Help. "What's Allowed" is the company's policy in plain words, which is a
 * thing you look up when you are unsure, exactly like the help pages.
 */
export const HelpHub: React.FC = () => (
  <AdoptionHub
    label="Help"
    tabs={[
      { id: 'help', label: 'Help & Community', icon: HelpCircle, render: () => <HelpCommunityPage /> },
      { id: 'allowed', label: "What's Allowed", icon: ShieldCheck, render: () => <WhatsAllowedPage /> },
    ]}
  />
);

/**
 * My readiness, with onboarding folded in. Onboarding is a task, not a place:
 * the tab is offered only while it is unfinished, which is the rule the sidebar
 * item already used.
 */
export const MyGrowthHub: React.FC = () => {
  const { context } = useParticipant();
  const unfinished = !!context && !context.readiness_complete;
  const tabs: HubTab[] = [
    { id: 'readiness', label: 'My Readiness', icon: Sparkles, render: () => <MyReadinessPage /> },
  ];
  if (unfinished) {
    tabs.push({ id: 'onboarding', label: 'Onboarding', icon: ListChecks, render: () => <OnboardingPage /> });
  }
  return <AdoptionHub label="My readiness" tabs={tabs} />;
};

/** A department head's three views of their own department. */
export const DepartmentHub: React.FC = () => (
  <AdoptionHub
    label="My department"
    tabs={[
      { id: 'overview', label: 'Department Overview', icon: Building2, render: () => <DepartmentPage /> },
      { id: 'rollup', label: 'Team Rollup', icon: BarChart3, perm: 'aai_view_department_rollup', render: () => <TeamRollupPage /> },
      { id: 'roster', label: 'Roster', icon: Users, render: () => <RosterPage /> },
    ]}
  />
);

/** The trainer's programme: the curriculum and the templates it is built from. */
export const ProgramHub: React.FC = () => (
  <AdoptionHub
    label="Program"
    tabs={[
      { id: 'curriculum', label: 'Program', icon: GraduationCap, perm: 'aai_manage_program', render: () => <CurriculumPage /> },
      { id: 'templates', label: 'Templates', icon: LayoutTemplate, perm: 'aai_manage_program', render: () => <TemplatesPage /> },
    ]}
  />
);
